/**
 * §F5.1: adopting the deployment's cold-boot basemap, once the server has named it.
 *
 * The basemap catalogue is served (`/meta/config.basemaps`, `apps/meta/basemaps.py`) because
 * the CARTO rows depend on a key the browser must never hold, and the **default** rides with
 * it: a deployment with real CARTO tiles boots on `light`, one without keeps OSM `streets`.
 * That answer arrives after the first render — `main.tsx` hydrates the store from the hash
 * before `createRoot`, and `/meta/config` is a network call — so something has to move the
 * map when it lands. This is that something, and it renders nothing.
 *
 * **It never overrules a statement.** `adoptDefaultBasemap` is a no-op once `basemapPinned`
 * is set, which a `bm=` link does on hydration and the §F5.10 panel does on every pick. The
 * only state it can move is the one nobody chose.
 *
 * A sibling of `PermalinkSync` for the same reason that component exists: reconciling the
 * store against a catalogue that answers later is a boot concern, not a map concern, and
 * `MapView` is not mounted on every route this can matter for (the §F4 splash draws its own
 * mini-map on `/`).
 */
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';

import { basemapsFromConfig } from '../features/map/basemaps';
import { fetchConfig, queryKeys } from '../lib/api';
import { useAppStore, type Basemap } from '../lib/state';

export function BasemapDefault(): null {
  const { data: config } = useQuery({ queryKey: queryKeys.config(), queryFn: fetchConfig });
  const adopt = useAppStore((s) => s.adoptDefaultBasemap);
  const served = config?.basemaps?.default;

  useEffect(() => {
    if (!served) return;
    // A `default` naming a row this payload does not carry would be a cold boot onto tiles
    // that do not exist. The server builds the two together and cannot disagree with itself,
    // but this is the client of a public API, not of a function call.
    const known = basemapsFromConfig(config).some((b) => b.key === served);
    if (known) adopt(served as Basemap);
  }, [served, config, adopt]);

  return null;
}

export default BasemapDefault;
